import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { forgetAllTrustedDevices, forgetTrustedDevice, listTrustedDevices, type TrustedDevice } from "@/api/client";
import { useSession } from "@/auth/session";
import { DataTable, type Column } from "@/components/DataTable";
import { formatDate } from "@/components/format";
import { EmptyState, ErrorBanner, ToolbarButton, WorkspaceHeader } from "@/components/ui";

const KEY = ["trusted-devices"];

export function AccountSecurityPage() {
  const { me } = useSession();
  const queryClient = useQueryClient();
  const devices = useQuery({ queryKey: KEY, queryFn: listTrustedDevices });
  const refresh = () => queryClient.invalidateQueries({ queryKey: KEY });
  const forgetOne = useMutation({ mutationFn: (id: string) => forgetTrustedDevice(id), onSuccess: refresh });
  const forgetAll = useMutation({ mutationFn: () => forgetAllTrustedDevices(), onSuccess: refresh });
  const items = devices.data?.items ?? [];
  const busy = forgetOne.isPending || forgetAll.isPending;

  const columns: Column<TrustedDevice>[] = [
    {
      key: "device",
      header: "Device",
      primary: true,
      cell: (device) => (device.current ? `${device.label} (this device)` : device.label),
    },
    { key: "added", header: "Added", cell: (device) => formatDate(device.created_at) },
    {
      key: "used",
      header: "Last used",
      cell: (device) => (device.last_used_at ? formatDate(device.last_used_at) : "Never"),
    },
    { key: "expires", header: "Expires", cell: (device) => formatDate(device.expires_at) },
    {
      key: "forget",
      header: "",
      className: "text-right",
      cell: (device) => (
        <ToolbarButton
          aria-label={`Forget this device: ${device.label}`}
          className="min-h-10 md:min-h-0"
          disabled={busy}
          onClick={() => forgetOne.mutate(device.id)}
        >
          Forget this device
        </ToolbarButton>
      ),
    },
  ];

  return (
    <section className="space-y-5">
      <WorkspaceHeader
        title="Account security"
        detail={
          me?.two_factor_required
            ? "Your role requires a sign-in code by email."
            : "Your role does not require a sign-in code."
        }
      />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-semibold">Trusted devices</h2>
        {items.length > 0 ? (
          <ToolbarButton disabled={busy} onClick={() => forgetAll.mutate()}>
            Forget all devices
          </ToolbarButton>
        ) : null}
      </div>
      <ErrorBanner error={devices.error ?? forgetOne.error ?? forgetAll.error} />
      {devices.isLoading ? (
        <EmptyState compact title="Loading devices" detail="Fetching your trusted devices." />
      ) : devices.data && items.length === 0 ? (
        <EmptyState compact title="No trusted devices." detail="Devices you choose to remember at sign-in appear here." />
      ) : items.length > 0 ? (
        <div className="md:overflow-hidden md:rounded-lg md:border md:border-zinc-200 md:bg-white">
          <DataTable rows={items} columns={columns} rowKey={(device) => device.id} label="Trusted devices" />
        </div>
      ) : null}
    </section>
  );
}
